import { Dispatch, FC, SetStateAction, useState } from 'react';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupDatepicker from 'components/ui/FormGroupDatepicker/FormGroupDatepicker';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import Button from 'components/ui/Button/Button';
import { faCalendar, faCopy, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { formatISO } from 'date-fns';
import { addYears, dateFormat, showError } from 'utils/utils';
import withErrorBoundary from '../../withErrorBoundary';
import { MyOrder, Pax } from 'ts/shared/interfaces/MOResponse';
import { changeSegmentEditor, getRandomName } from 'services/myOrders';
import ConfirmDialog from '../../ConfirmDialog';
import { getGender, paxTypeOptions, titlePassengerOptions } from 'constants/constant';
import { useTranslation } from 'react-i18next';
import Tabs from 'components/ui/Tabs/Tabs';

interface MoPaxesTabProps {
  myOrder?: MyOrder;
  orderModel: any;
  setOrderModel: Dispatch<SetStateAction<any>>;
  paxesModel: Pax[];
  setPaxesModel: Dispatch<SetStateAction<Pax[]>>;
  activePaxIndex: number;
  setActivePaxIndex: (index: number) => void;
  isLoading: boolean;
  setIsLoading: (value: boolean) => void;
  syncFromResult: (result: MyOrder) => void;
  onSubmit: (myOrder: MyOrder) => void;
}

const MoPaxesTab: FC<MoPaxesTabProps> = ({
  myOrder,
  orderModel,
  setOrderModel,
  paxesModel,
  setPaxesModel,
  activePaxIndex,
  setActivePaxIndex,
  isLoading,
  setIsLoading,
  syncFromResult,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const [activePaxTab, setActivePaxTab] = useState(0);
  const [loadingNewPax, setLoadingNewPax] = useState(false);
  const [showConfirmCopyPax, setShowConfirmCopyPax] = useState(false);
  const [showConfirmDeletePax, setShowConfirmDeletePax] = useState(false);
  const [copyPaxIndex, setCopyPaxIndex] = useState<number | null>(null);
  const [deletePaxIndex, setDeletePaxIndex] = useState<number | null>(null);
  const selectedPax = paxesModel[activePaxIndex];
  const activeSegment = orderModel?.segments?.find((segment: any) => segment.orderSegId === myOrder?.fullSegment?.orderSegId);
  const assignedPaxCodes = new Set<number>(
    (activeSegment?.rooms || [])
      .filter((room: any) => (room.delRow || 0) === 0)
      .flatMap((room: any) => room.paxCodes || []),
  );

  const submitResult = (result: MyOrder) => {
    if (!myOrder) return;
    onSubmit({ ...result, orderRow: { ...result.orderRow, status: myOrder.orderRow.status } });
  };

  const onChangeSelectedPax = (field: keyof Pax, value: string | number) => {
    setPaxesModel(prev => {
      const newPaxes = prev.map((pax, index) => (
        index === activePaxIndex ? { ...pax, [field]: value } : pax
      ));
      setOrderModel((prevOrder: any) => prevOrder ? { ...prevOrder, paxes: newPaxes } : prevOrder);
      return newPaxes;
    });
  };

  const addNewPax = async () => {
    if (!myOrder || !orderModel) return;
    setLoadingNewPax(true);
    const randomName = await getRandomName();
    const generatedPax = randomName?.maleNames?.[0];

    if (!generatedPax) {
      showError(t('something_went_wrong'));
      setLoadingNewPax(false);
      return;
    }
    const templatePax = paxesModel[0];
    if (!templatePax) {
      showError(t('something_went_wrong'));
      setLoadingNewPax(false);
      return;
    }

    const nextPaxCode = paxesModel.length > 0 ? Math.max(...paxesModel.map(p => Number(p.paxCode) || 0)) + 1 : 1;
    const generatedFirstName = generatedPax.firstName || templatePax.firstName || '';
    const generatedLastName = generatedPax.lastName || templatePax.lastName || '';
    const fullName = `${generatedFirstName} ${generatedLastName}`.trim();

    const newPax: Pax = {
      ...templatePax,
      paxCode: nextPaxCode,
      delRow: 0,
      firstName: generatedFirstName,
      firstDisplayName: generatedFirstName,
      lastName: generatedLastName,
      fullName,
      fullDisplayName: fullName,
      dob: formatISO(addYears(-40)),
    };

    const newPaxes = [...paxesModel, newPax];
    const oldPaxes = paxesModel;
    const oldOrder = orderModel;
    const nextOrder = { ...orderModel, paxes: newPaxes };

    setPaxesModel(newPaxes);
    setOrderModel(nextOrder);
    setActivePaxIndex(newPaxes.length - 1);
    const result = await changeSegmentEditor({
      order: nextOrder,
      changePrice: true,
    });
    if (result) {
      syncFromResult(result);
      submitResult(result);
    } else {
      setPaxesModel(oldPaxes);
      setOrderModel(oldOrder);
      showError(t('something_went_wrong'));
    }
    setLoadingNewPax(false);
  };

  const onClickSubmitCopyPax = async () => {
    if (!myOrder || !orderModel) return;
    const sourceIndex = copyPaxIndex ?? activePaxIndex;
    const sourcePax = paxesModel[sourceIndex];
    if (!sourcePax) return;

    const { paxCode, id, ...paxWithoutId } = sourcePax as any;
    const paxWithId = { ...paxWithoutId, paxCode: paxesModel.length + 1, delRow: 0 };
    const newPaxes = [...paxesModel, paxWithId];
    const oldPaxes = paxesModel;
    const oldOrder = orderModel;
    const nextOrder = { ...orderModel, paxes: newPaxes };

    setPaxesModel(newPaxes);
    setOrderModel(nextOrder);
    setActivePaxIndex(newPaxes.length - 1);
    setShowConfirmCopyPax(false);
    setCopyPaxIndex(null);
    setIsLoading(true);

    const result = await changeSegmentEditor({
      order: nextOrder,
      changePrice: true,
    });

    if (result) {
      syncFromResult(result);
      submitResult(result);
    } else {
      setPaxesModel(oldPaxes);
      setOrderModel(oldOrder);
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };

  const onChangePaxCode = (value: string) => {
    const getPaxTitleByCode = () => {
      if (['CNN', 'YTH', 'INF', 'INS'].includes(value)) return 'child';
      return 'mr';
    }
    const getDobByCode = () => {
      if (value === 'CNN') return formatISO(addYears(-16));
      if (value === 'ADT') return formatISO(addYears(-40));
      if (value === 'YTH') return formatISO(addYears(-18));
      if (value === 'INF') return formatISO(addYears(-1));
      if (value === 'INS') return formatISO(addYears(-1));
      return formatISO(addYears(-40));
    }
    setPaxesModel(prev => {
      const newPaxes = prev.map((pax, index) => (
        index === activePaxIndex ? { ...pax, paxTypeCode: value, paxTitle: getPaxTitleByCode(), dob: getDobByCode() } : pax
      ));
      setOrderModel((prevOrder: any) => prevOrder ? { ...prevOrder, paxes: newPaxes } : prevOrder);
      return newPaxes;
    });

  }

  const onClickSubmitDeletePax = async () => {
    if (!myOrder || !orderModel) return;
    const sourceIndex = deletePaxIndex ?? activePaxIndex;
    if (sourceIndex < 0 || sourceIndex >= paxesModel.length) return;

    const newPaxes = paxesModel.map((pax, index) => (
      index === sourceIndex ? { ...pax, delRow: 1 } : pax
    ));
    const oldPaxes = paxesModel;
    const oldOrder = orderModel;
    const nextOrder = { ...orderModel, paxes: newPaxes };

    setPaxesModel(newPaxes);
    setOrderModel(nextOrder);
    setShowConfirmDeletePax(false);
    setDeletePaxIndex(null);
    setIsLoading(true);

    const result = await changeSegmentEditor({
      order: nextOrder,
      changePrice: true,
    });

    if (result) {
      syncFromResult(result);
      submitResult(result);
    } else {
      setPaxesModel(oldPaxes);
      setOrderModel(oldOrder);
      showError(t('something_went_wrong'));
    }
    setIsLoading(false);
  };


  const paxRoomIdsByCode = new Map<number, number>();
  (activeSegment?.rooms || [])
    .filter((room: any) => (room.delRow || 0) === 0)
    .forEach((room: any) => {
      (room.paxCodes || []).forEach((paxCode: number) => {
        if (!paxRoomIdsByCode.has(paxCode)) {
          paxRoomIdsByCode.set(paxCode, room.orderSegDetailId);
        }
      });
    });

  return (
    <div>
      <div className="mo-block mt-10">
        <div className="mo-block-title">Paxes</div>
        <div className="mo-block-content flex-column">
          <div className="ml-a mb-10">
            <Button
              title={'Add new pax'}
              onClick={() => addNewPax()}
              className="btn btn-default btn-sm"
              icon={faPlus}
              disabled={loadingNewPax}
              iconclassname="mr-5"
            />
          </div>
          <div className="table-default table-small mb-10 text-sm">
            <table className="table-bordered-lines table-mobile">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Order Id</th>
                  <th>Room Id</th>
                  <th>Pax Title</th>
                  <th>Pax Type</th>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th style={{ width: '100px' }}>Actions</th>
                </tr>
              </thead>
              <tbody>               {paxesModel
                .map((pax, realIndex) => ({ pax, realIndex }))
                .filter(({ pax }) => (pax.delRow || 0) === 0)
                .map(({ pax, realIndex }) => {
                  const isPaxAssignedToRoom = assignedPaxCodes.has(pax.paxCode);
                  return (
                    <tr
                      key={pax.paxCode}
                      className={activePaxIndex === realIndex ? 'active' : ''}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setActivePaxIndex(realIndex)}
                    >
                      <td>{pax.paxCode}</td>
                      <td>{pax.orderId}</td>
                      <td>{paxRoomIdsByCode.get(pax.paxCode) || ''}</td>
                      <td>{pax.paxTitle}</td>
                      <td>{pax.paxTypeCode}</td>
                      <td>{pax.firstName}</td>
                      <td>{pax.lastName}</td>
                      <td>
                        <Button
                          title={''}
                          icon={faCopy}
                          className="btn btn-default btn-sm mr-5"
                          onClick={() => {
                            setCopyPaxIndex(realIndex);
                            setShowConfirmCopyPax(true);
                          }}
                        />
                        <Button
                          title={''}
                          icon={faTrash}
                          className="btn btn-danger btn-sm"
                          disabled={isPaxAssignedToRoom}
                          onClick={() => {
                            setDeletePaxIndex(realIndex);
                            setShowConfirmDeletePax(true);
                          }}
                        />
                      </td>
                    </tr>
                  )
                })}
              </tbody>

            </table>
          </div>
        </div>
      </div>

      <div className="mt-20">
        <Tabs
        size="small"
          label="Pax"
          value={String(activePaxTab)}
          onValueChange={(v) => setActivePaxTab(Number(v))}
          className="mb-10 room-tabs"
          items={[
            { value: '0', label: 'Pax Details' },
            { value: '1', label: 'Pax Contacts' },
            { value: '2', label: 'Pax General' },
          ]}
        />
        <div className="tab-content">
          <div className="tab-pane active">
            {activePaxTab === 0 && (
              <div className="mo-block-content">
                <div className="row">
                  <div className="col-md-3 col-6 mb-15">
                    <FormGroup
                      onChange={() => { }}
                      value={selectedPax?.paxCode?.toString() || ''}
                      label={'Pax Code'}
                      movablePlaceholder={'Pax Code'}
                      allowOnlyNumbers
                    />
                  </div>
                  <div className="col-md-3 col-6 mb-15">
                    <SelectCustom
                      onChange={(value) => onChangeSelectedPax('paxTitle', value)}
                      value={selectedPax?.paxTitle || ''}
                      options={titlePassengerOptions}
                      movablePlaceholder={'Pax Title'}
                      needToTranslate={true}
                      appendToBody
                    />
                  </div>
                  <div className="col-md-3 col-6 mb-15">
                    <SelectCustom
                      onChange={(value) => onChangePaxCode(value)}
                      value={selectedPax?.paxTypeCode || ''}
                      options={paxTypeOptions}
                      movablePlaceholder={'Pax Type Code'}
                    />
                  </div>
                  <div className="col-md-3 col-6 mb-15">
                    <SelectCustom
                      onChange={(value) => onChangeSelectedPax('gender', value)}
                      value={selectedPax?.gender || ''}
                      options={getGender()}
                      movablePlaceholder={'Gender'}
                      needToTranslate={true}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('firstName', value)}
                      value={selectedPax?.firstName || ''}
                      label={'First Name'}
                      movablePlaceholder={'First Name'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('lastName', value)}
                      value={selectedPax?.lastName || ''}
                      label={'Last Name'}
                      movablePlaceholder={'Last Name'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15 form-group-calendar-top">
                    <FormGroupDatepicker
                      onChange={(value) => onChangeSelectedPax('dob', formatISO(value))}
                      value={new Date(selectedPax?.dob || '')}
                      movablePlaceholderIcon={faCalendar}
                      label={'DOB'}
                      movablePlaceholder={'DOB'}
                    />
                  </div>
                  <div className="col-md-6"></div>
                  <div className="col-md-6 col-12 mb-15 form-group-calendar-top">
                    <FormGroupDatepicker
                      onChange={(value) => onChangeSelectedPax('passportValidTill', formatISO(value))}
                      value={new Date(selectedPax?.passportValidTill || '')}
                      label={'Passport Valid Till'}
                      movablePlaceholder={'Passport Valid Till'}
                      movablePlaceholderIcon={faCalendar}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('passportNumber', value)}
                      value={selectedPax?.passportNumber || ''}
                      label={'Passport Number'}
                      movablePlaceholder={'Passport Number'}
                    />
                  </div>
                </div>
              </div>
            )}
            {activePaxTab === 1 && (
              <div className="mo-block-content">
                <div className="row">
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('phone1', value)}
                      value={selectedPax?.phone1 || ''}
                      label={'Phone 1'}
                      movablePlaceholder={'Phone 1'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('phone2', value)}
                      value={selectedPax?.phone2 || ''}
                      label={'Phone 2'}
                      movablePlaceholder={'Phone 2'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('phone3', value)}
                      value={selectedPax?.phone3 || ''}
                      label={'Phone 3'}
                      movablePlaceholder={'Phone 3'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15"></div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('email1', value)}
                      value={selectedPax?.email1 || ''}
                      label={'Email 1'}
                      movablePlaceholder={'Email 1'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('email2', value)}
                      value={selectedPax?.email2 || ''}
                      label={'Email 2'}
                      movablePlaceholder={'Email 2'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('mobile1', value)}
                      value={selectedPax?.mobile1 || ''}
                      label={'Mobile 1'}
                      movablePlaceholder={'Mobile 1'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('mobile2', value)}
                      value={selectedPax?.mobile2 || ''}
                      label={'Mobile 2'}
                      movablePlaceholder={'Mobile 2'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('address1', value)}
                      value={selectedPax?.address1 || ''}
                      label={'Address 1'}
                      movablePlaceholder={'Address 1'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('address2', value)}
                      value={selectedPax?.address2 || ''}
                      label={'Address 2'}
                      movablePlaceholder={'Address 2'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('city', value)}
                      value={selectedPax?.city || ''}
                      label={'City'}
                      movablePlaceholder={'City'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('country', value)}
                      value={selectedPax?.country || ''}
                      label={'Country'}
                      movablePlaceholder={'Country'}
                    />
                  </div>
                </div>
              </div>
            )}
            {activePaxTab === 2 && (
              <div className="mo-block-content">
                <div className="row">
                  <div className="col-md-3 col-6 mb-15">
                    <FormGroup
                      onChange={() => { }}
                      value={selectedPax?.userCodeC?.toString() || ''}
                      label={'User Code C'}
                      movablePlaceholder={'User Code C'}
                      disabled={true}
                    />
                  </div>
                  <div className="col-md-3 col-6 mb-15">
                    <FormGroup
                      onChange={() => { }}
                      value={selectedPax?.userCodeU?.toString() || ''}
                      label={'User Code U'}
                      movablePlaceholder={'User Code U'}
                      disabled={true}
                    />
                  </div>
                  <div className="col-md-3 col-6 mb-15">
                    <FormGroup
                      onChange={() => { }}
                      value={selectedPax?.dateC ? dateFormat(selectedPax.dateC, 'dd/MM/yy HH:mm') : ''}
                      label={'Date C'}
                      movablePlaceholder={'Date C'}
                      disabled={true}
                    />
                  </div>
                  <div className="col-md-3 col-6 mb-15">
                    <FormGroup
                      onChange={() => { }}
                      value={selectedPax?.dateU ? dateFormat(selectedPax.dateU, 'dd/MM/yy HH:mm') : ''}
                      label={'Date U'}
                      movablePlaceholder={'Date U'}
                      disabled={true}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('note1', value)}
                      value={selectedPax?.note1 || ''}
                      label={'Note 1'}
                      movablePlaceholder={'Note 1'}
                    />
                  </div>
                  <div className="col-md-6 col-12 mb-15">
                    <FormGroup
                      onChange={(value) => onChangeSelectedPax('note2', value)}
                      value={selectedPax?.note2 || ''}
                      label={'Note 2'}
                      movablePlaceholder={'Note 2'}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {showConfirmCopyPax && (
        <ConfirmDialog
          title="Copy Pax"
          openModal={showConfirmCopyPax}
          close={() => {
            setShowConfirmCopyPax(false);
            setCopyPaxIndex(null);
          }}
          onConfirm={() => onClickSubmitCopyPax()}
        />
      )}
      {showConfirmDeletePax && (
        <ConfirmDialog
          title="Delete Pax"
          openModal={showConfirmDeletePax}
          close={() => {
            setShowConfirmDeletePax(false);
            setDeletePaxIndex(null);
          }}
          onConfirm={() => onClickSubmitDeletePax()}
        />
      )}
    </div>
  );
};

export default withErrorBoundary(MoPaxesTab, 'MoPaxesTab');
